if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('[role="document"]');
        this.thumbnails = this.querySelector('.product-media-modal__thumbnails');
        if (!this.thumbnails) return;

        this.thumbList = this.thumbnails.querySelector('.product-media-modal__thumbs');
        this.prevButton = this.thumbnails.querySelector('button[name="previous"]');
        this.nextButton = this.thumbnails.querySelector('button[name="next"]');

        // ModalDialog closes media modals on any mouse pointerup; keep the strip clickable.
        this.thumbnails.addEventListener('pointerup', (event) => event.stopPropagation());
        this.thumbList.addEventListener('click', (event) => {
          const thumb = event.target.closest('button[data-target]');
          if (thumb) this.setActiveMedia(thumb.dataset.target);
        });
        this.prevButton.addEventListener('click', () => this.scrollThumbnails(-1));
        this.nextButton.addEventListener('click', () => this.scrollThumbnails(1));
        this.thumbList.addEventListener('scroll', this.updateThumbButtons.bind(this), { passive: true });
        window.addEventListener('resize', this.updateThumbButtons.bind(this));
      }

      hide() {
        super.hide();
      }

      show(opener) {
        // Build before super.show() so trapFocus picks up the thumbnails.
        this.buildThumbnails();
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const activeMedia = this.setActiveMedia(this.openedBy.getAttribute('data-media-id'));
        if (!activeMedia || window.matchMedia('(min-width: 750px)').matches) return;

        activeMedia.scrollIntoView();
        this.content.scrollLeft = (activeMedia.width - this.content.clientWidth) / 2;
      }

      setActiveMedia(mediaId) {
        const activeMedia = this.content.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return null;

        window.pauseAllMedia();
        this.content.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });

        if (this.thumbList) {
          let activeThumb = null;
          this.thumbList.querySelectorAll('button[data-target]').forEach((thumb) => {
            const isActive = thumb.dataset.target === mediaId;
            if (isActive) {
              thumb.setAttribute('aria-current', 'true');
              activeThumb = thumb;
            } else {
              thumb.removeAttribute('aria-current');
            }
          });
          if (activeThumb) this.scrollThumbIntoView(activeThumb.parentElement);
        }

        // Gallery videos open here rather than playing inline, so start any video
        // (hosted or external) straight away — the opener click allows sound.
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('video, iframe')
        )
          activeMedia.loadContent();

        return activeMedia;
      }

      buildThumbnails() {
        if (!this.thumbnails) return;

        const slides = Array.from(this.content.children).filter((slide) => slide.hasAttribute('data-media-id'));
        const showStrip = slides.length > 1 && window.matchMedia('(min-width: 750px)').matches;
        this.thumbnails.hidden = !showStrip;
        this.classList.toggle('product-media-modal--has-thumbnails', showStrip);

        // Mobile keeps its single-slide lightbox; leave nothing for trapFocus to pick up.
        if (!showStrip) {
          this.thumbList.replaceChildren();
          this.thumbnailsKey = null;
          this.prevButton.disabled = true;
          this.nextButton.disabled = true;
          return;
        }

        const key = slides.map((slide) => slide.dataset.mediaId).join(',');
        if (key === this.thumbnailsKey) return this.updateThumbButtons();
        this.thumbnailsKey = key;

        const counts = { image: 0, video: 0, model: 0 };
        this.thumbList.replaceChildren(
          ...slides.map((slide) => {
            const type = slide.nodeName == 'DEFERRED-MEDIA' ? 'video' : slide.nodeName == 'IMG' ? 'image' : 'model';
            counts[type] += 1;

            const item = document.createElement('li');
            item.className = 'product-media-modal__thumb-item';

            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'thumbnail product-media-modal__thumb';
            button.dataset.target = slide.dataset.mediaId;
            button.setAttribute('aria-label', this.thumbnails.dataset[`label${type[0].toUpperCase()}${type.slice(1)}`].replace('[index]', counts[type]));

            // The strip only shows from 750px, so prefer a slide's desktop-only poster
            // (the application video's desktop cut) over its mobile one.
            const source = slide.nodeName == 'IMG' ? slide : slide.querySelector('img.small-hide') || slide.querySelector('img');
            if (source) {
              const image = source.cloneNode();
              image.removeAttribute('data-media-id');
              image.removeAttribute('id');
              image.removeAttribute('class');
              image.alt = '';
              image.sizes = '120px';
              image.loading = 'lazy';
              button.appendChild(image);
            }

            const badge = this.thumbnails.querySelector(`template[data-badge="${type}"]`);
            if (badge) item.appendChild(badge.content.cloneNode(true));

            item.appendChild(button);
            return item;
          })
        );
        this.updateThumbButtons();
      }

      scrollThumbnails(direction) {
        this.thumbList.scrollBy({ left: direction * this.thumbList.clientWidth * 0.8 });
      }

      scrollThumbIntoView(item) {
        const list = this.thumbList;
        if (item.offsetLeft >= list.scrollLeft && item.offsetLeft + item.offsetWidth <= list.scrollLeft + list.clientWidth) return;
        list.scrollTo({ left: item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2 });
      }

      updateThumbButtons() {
        if (!this.thumbList) return;
        const maxScroll = this.thumbList.scrollWidth - this.thumbList.clientWidth;
        this.prevButton.disabled = this.thumbList.scrollLeft <= 1;
        this.nextButton.disabled = this.thumbList.scrollLeft >= maxScroll - 1;
      }
    }
  );
}
